import React, { useState, useEffect, useCallback } from 'react';
import { FileSpreadsheet, RefreshCw, Download } from 'lucide-react';
import api from '../../services/api';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const TREATMENT_LABELS = {
  intra_state: 'Intra-State (CGST+SGST)', inter_state: 'Inter-State (IGST)',
  export: 'Export / Out of Scope', unregistered: 'No GST',
};

export default function GstReportPage() {
  const [groupBy, setGroupBy] = useState('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const currency = useCompanyCurrency();

  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/invoices/reports/gst', { params: { groupBy, from: from || undefined, to: to || undefined } })
      .then(res => setData(res.data))
      .finally(() => setLoading(false));
  }, [groupBy, from, to]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    if (!data?.rows?.length) return;
    const header = 'Period,Taxable Value,CGST,SGST,IGST,Total Tax,Invoice Count,Invoice Value\n';
    const rows = data.rows.map(r => [r.period, r.taxableValue, r.cgst, r.sgst, r.igst, r.totalTax, r.invoiceCount, r.invoiceValue].join(',')).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gst-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={PAGE}>
      <PageHeader title="GST Report" subtitle="Taxable value and CGST/SGST/IGST collected on customer invoices, grouped by month or day — a GSTR-1-style summary based on invoice issue date." icon={FileSpreadsheet} />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B]">Taxable value</p>
          <p className="text-xl font-bold text-[#2E2E2E]">{formatCurrency(data?.totalTaxableValue || 0, currency)}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B]">CGST + SGST</p>
          <p className="text-xl font-bold text-[#2E2E2E]">{formatCurrency((data?.totalCgst || 0) + (data?.totalSgst || 0), currency)}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B]">IGST</p>
          <p className="text-xl font-bold text-[#2E2E2E]">{formatCurrency(data?.totalIgst || 0, currency)}</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <p className="text-xs text-[#706E6B]">Total tax collected</p>
          <p className="text-xl font-bold text-[#2E2E2E]">{formatCurrency(data?.totalTax || 0, currency)}</p>
        </div>
      </div>

      {data?.byTreatment && (
        <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
          <h3 className="text-sm font-semibold text-[#2E2E2E] mb-3">Invoices by Supply Type</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(TREATMENT_LABELS).map(([key, label]) => (
              <div key={key}>
                <p className="text-xs text-[#706E6B]">{label}</p>
                <p className="text-lg font-bold text-[#2E2E2E]">{data.byTreatment[key] || 0}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
        <div className="flex items-center gap-2 mb-3">
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)} className="h-9 px-2 border border-[#D8DDE6] rounded text-sm focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
            <option value="month">By Month</option>
            <option value="day">By Day</option>
          </select>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-9 px-2 border border-[#D8DDE6] rounded text-sm focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-9 px-2 border border-[#D8DDE6] rounded text-sm focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
          <button onClick={load} disabled={loading} className="h-9 px-3 border border-[#E5E5E5] rounded text-xs font-semibold text-[#5C5C5C] hover:bg-[#F3F3F3] disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap shrink-0">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={handleExport} className="h-9 px-3 border border-[#E5E5E5] rounded text-xs font-semibold text-[#5C5C5C] hover:bg-[#F3F3F3] flex items-center gap-1.5 ml-auto whitespace-nowrap shrink-0">
            <Download size={13} /> Export CSV
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E5E5]">
                <th className="text-left py-2 pr-4 font-semibold text-[#5C5C5C]">Period</th>
                <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">Taxable Value</th>
                <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">CGST</th>
                <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">SGST</th>
                <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">IGST</th>
                <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">Total Tax</th>
                <th className="text-right py-2 pr-4 font-semibold text-[#5C5C5C]">Invoices</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F2F2]">
              {(data?.rows || []).sort((a, b) => b.period.localeCompare(a.period)).map(r => (
                <tr key={r.period}>
                  <td className="py-2.5 pr-4 font-medium text-[#2E2E2E]">{r.period}</td>
                  <td className="py-2.5 pr-4 text-right text-[#2E2E2E]">{formatCurrency(r.taxableValue, currency)}</td>
                  <td className="py-2.5 pr-4 text-right text-[#2E2E2E]">{formatCurrency(r.cgst, currency)}</td>
                  <td className="py-2.5 pr-4 text-right text-[#2E2E2E]">{formatCurrency(r.sgst, currency)}</td>
                  <td className="py-2.5 pr-4 text-right text-[#2E2E2E]">{formatCurrency(r.igst, currency)}</td>
                  <td className="py-2.5 pr-4 text-right font-semibold text-[#2E2E2E]">{formatCurrency(r.totalTax, currency)}</td>
                  <td className="py-2.5 pr-4 text-right text-[#5C5C5C]">{r.invoiceCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && (!data?.rows || data.rows.length === 0) && <p className="text-sm text-[#706E6B] py-8 text-center">No GST activity yet.</p>}
        </div>
      </div>
    </div>
  );
}
